<script setup>
import { computed, onMounted, ref } from 'vue';
import { ArrowLeft, CreditCard, ChevronDown, ShieldCheck } from 'lucide-vue-next';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import MobileShell from '../../components/MobileShell.vue';
import { state, applyCardSummary } from '../../data/store';
import { api } from '../../services/api';

const { t } = useI18n();
const router = useRouter();
const cardId = ref('');
const amount = ref('');
const loading = ref(true);
const success = ref(false);
const error = ref('');
const selectedCard = computed(() => state.cards.find((card) => card.id === cardId.value));

onMounted(async () => {
  try { applyCardSummary(await api.cardSummary()); cardId.value = state.cards[0]?.id || ''; }
  catch (reason) { applyCardSummary({}); error.value = reason.message || t('common.failed'); }
  finally { loading.value = false; }
});

async function submit() {
  if (!selectedCard.value || !Number(amount.value)) return;
  loading.value = true; error.value = '';
  try { const result = await api.withdrawCard(cardId.value, amount.value); success.value = Boolean(result.ok); }
  catch (reason) { error.value = reason.message || t('common.failed'); }
  finally { loading.value = false; }
}
</script>

<template>
  <MobileShell :nav="false" scrollable>
    <header class="screen-header"><button class="back-button" type="button" @click="router.back()"><ArrowLeft :size="28" /></button><h1 class="screen-title">{{ t('withdraw.title') }}</h1></header>
    <form class="withdraw-form" @submit.prevent="submit">
      <label class="field-group"><span class="field-label">{{ t('withdraw.card') }}</span><span class="field-shell"><CreditCard class="leading" :size="21"/><select v-model="cardId" class="field" required><option value="" disabled>—</option><option v-for="card in state.cards" :key="card.id" :value="card.id">{{ card.number }} · {{ Number(card.balance).toFixed(2) }} USD</option></select><ChevronDown class="trailing" :size="21"/></span></label>
      <label class="field-group"><span class="field-label-row"><span class="field-label">{{ t('withdraw.amount') }}</span><span class="field-hint">{{ t('withdraw.available',{amount:Number(selectedCard?.balance||0).toFixed(2)}) }}</span></span><span class="field-shell"><CreditCard class="leading" :size="21"/><input v-model="amount" class="field" type="number" min="0.01" :max="selectedCard?.balance || 0" step="0.01" inputmode="decimal"/><span class="trailing currency">USD</span></span></label>
      <section class="settlement-note"><ShieldCheck :size="20"/><p>{{ t('withdraw.settlement') }}</p></section>
      <p v-if="!loading && !state.cards.length" class="field-error">{{ t('card.noCards') }}</p>
      <p v-if="error" class="field-error">{{ error }}</p>
      <button class="primary-button" :disabled="loading || !selectedCard || !Number(amount)" type="submit">{{ loading?t('common.loading'):t('withdraw.action') }}</button>
    </form>
    <div v-if="success" class="modal-backdrop" @click.self="success=false"><section class="success-modal"><img src="/assets/wallet-success.png" alt=""/><h2>{{ t('common.success') }}</h2><p>{{ t('withdraw.pending') }}</p><button class="secondary-button" type="button" @click="success=false">{{ t('common.confirm') }}</button></section></div>
  </MobileShell>
</template>

<style scoped>
.screen-header{margin-bottom:24px}.withdraw-form{display:grid;gap:20px}.field-group{display:block}.field-label{margin-bottom:8px}.field-shell select{appearance:none}.currency{color:#fff!important;font-size:12px}.settlement-note{display:flex;gap:11px;padding:15px;border:1px solid rgba(106,89,255,.25);border-radius:14px;background:rgba(90,69,254,.09);color:#a99cff}.settlement-note svg{flex:0 0 auto}.settlement-note p{margin:0;color:#aaa8b3;font-size:12px;line-height:1.65}
</style>
